// ImageManager.jsx  
// Component for handling image upload, preview, and removal

import React, { useRef, useState } from 'react';
import { styles } from './styles';

// SVG Icons
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const ImageManager = ({ previews, onRemoveImage, onNewImages }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onNewImages(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onNewImages(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={styles.imageManagerContainer}>
      {/* Upload Area */}
      <div 
        style={{
          ...styles.uploadArea,
          ...(isDragging ? styles.uploadAreaDragging : {})
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div style={styles.uploadIcon}>
          <UploadIcon />
        </div>
        <p style={styles.uploadText}>
          Drag and drop images here or click to browse
        </p>
        <p style={styles.uploadHint}>
          <ImageIcon /> Maximum 10 images (JPG, PNG, GIF)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={styles.hiddenInput}
        />
      </div>
      
      {/* Image Preview Grid */}
      {previews.length > 0 && (
        <div style={styles.previewSection}>
          <p style={styles.previewLabel}>
            Current Images ({previews.length}/10)
          </p>
          <div style={styles.imageGrid}>
            {previews.map((src, index) => (
              <ImagePreview
                key={index}
                src={src}
                index={index}
                onRemove={() => onRemoveImage(src)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ImagePreview = ({ src, index, onRemove }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getImageSrc = (src) => {
    return typeof src === 'string' ? src : URL.createObjectURL(src);
  };

  return (
    <div 
      style={styles.imagePreviewItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={getImageSrc(src)}
        alt={`Preview ${index + 1}`}
        style={styles.imagePreview}
      />
      {index === 0 && (
        <div style={styles.primaryBadge}>Primary</div>
      )}
      <button
        type="button"
        onClick={onRemove}
        style={{
          ...styles.removeImageButton,
          opacity: isHovered ? 1 : 0.7
        }}
        aria-label="Remove image"
      >
        <TrashIcon />
      </button>
    </div>
  );
};

export default ImageManager;